import RefreshIcon from '@assets/buttons-icons/refresh.svg?react';
import { QuickActionProps } from '@components/suggestions/quickActions/types';
import CheckIcon from '@lib/core/src/assets/selectIcon/checkIcon.svg';
import Editor from '@lib/core/src/components/Editor';
import SingleSelect from '@lib/core/src/components/singleSelected/SingleSelected';
import { IconButton, CircularProgress, ListItemIcon, Typography } from '@mui/material';

import UserSelect from '@/components/UserSelect/UserSelect';

import { Row, UserSelectContainer } from './AgentStyle';
import AgentWrapper from './AgentWrapper';
import QuickActionAnimationWrapper from './QuickActionAnimationWrapper';
import { EditorWrapper } from './QuickActionStyle';
import useEmailQuickActionLogic, { EmailTypes } from './hooks/useEmailQuickActionLogic';

const agentText = {
  title: 'Found this email on ',
  text: 'Do you want me to reply or forward this?'
};

const EmailQuickAction = ({
  quickAction,
  index,
  firstQuickActionControls,
  secondQuickActionControls
}: QuickActionProps) => {
  const {
    emailThread,
    emailThreadLoading,
    isGenerateEmailRespondPending,
    emailType,
    setEmailType,
    emailMessage,
    setEmailMessage,
    users,
    selectedUsers,
    setSelectedUsers,
    reGenerateMessage,
    emailBody
  } = useEmailQuickActionLogic({
    quickAction,
    index
  });

  const emailTypeOptions = Object.values(EmailTypes).map(value => ({
    label: value,
    value
  }));

  return (
    <QuickActionAnimationWrapper
      quickActionId={quickAction.id!}
      index={index}
      firstQuickActionControls={firstQuickActionControls}
      secondQuickActionControls={secondQuickActionControls}
    >
      <AgentWrapper
        title={agentText.title}
        titleUnderlined={emailThread?.subject || ''}
        emailBodyHtml={emailBody}
        bubbleText={agentText.text}
      >
        <Row>
          <SingleSelect
            value={emailTypeOptions.find(opt => opt.value === emailType)?.value || emailType}
            options={emailTypeOptions}
            onChange={setEmailType}
            MenuProps={{
              disablePortal: true,
              PaperProps: { sx: { zIndex: theme => theme.zIndex.modal + 1 } }
            }}
            sx={{
              width: '105px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: 'white',
              border: '1px solid #E0E0E0',
              fontWeight: 500,
              fontSize: '14px',
              color: '#212121',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& .MuiSelect-select': {
                padding: '6px 12px'
              }
            }}
            renderValue={val => (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{val}</div>
            )}
            renderOption={(option, selected) => (
              <div
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '6px 10px',
                  minHeight: 36,
                  fontSize: 14,
                  fontWeight: selected ? 600 : 400,
                  borderRadius: 8,
                  backgroundColor: selected ? '#F5F5F5' : 'transparent',
                  transition: 'background-color .2s ease'
                }}
              >
                <Typography sx={{ flexGrow: 1 }}>{option.label}</Typography>

                {selected && (
                  <ListItemIcon sx={{ minWidth: 0, m: 0, display: 'flex', alignItems: 'center' }}>
                    <img src={CheckIcon} width={14} height={14} alt="check" />
                  </ListItemIcon>
                )}
              </div>
            )}
          />
          <UserSelectContainer>
            <UserSelect
              disabled={emailThreadLoading || isGenerateEmailRespondPending}
              users={users}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
            />
          </UserSelectContainer>
        </Row>
      </AgentWrapper>
      {emailThreadLoading || isGenerateEmailRespondPending ? (
        <CircularProgress sx={{ margin: 'auto' }} />
      ) : (
        <EditorWrapper>
          <IconButton
            disabled={emailThreadLoading || isGenerateEmailRespondPending}
            sx={{ position: 'absolute', top: '3px', right: '0px' }}
            onClick={reGenerateMessage}
          >
            <RefreshIcon />
          </IconButton>
          <Editor value={emailMessage} onChange={setEmailMessage} minHeight="360px" />
        </EditorWrapper>
      )}
    </QuickActionAnimationWrapper>
  );
};

export default EmailQuickAction;
